import { useCallback, useEffect, useState } from 'react';
import { InteractionApiError } from '@/lib/interaction-api';
import type { createRecoveryApi } from '@/lib/recovery-api';

/**
 * Fetches the recovery surface's CSRF token, keeping the failure visible and
 * recoverable.
 *
 * Both recovery pages used to collapse every bootstrap failure to 'network'
 * and then early-return from submit while the token was missing — so a
 * locked-out user whose connection blipped typed a new password, pressed
 * "Save password", and nothing happened at all: no spinner, no error, no way
 * forward short of guessing that a reload might help. The real code matters
 * too: `/recovery/csrf` genuinely answers 429, and telling that user to check
 * their connection sends them debugging the wrong thing.
 */
export function useRecoveryCsrf(api: ReturnType<typeof createRecoveryApi>): {
  csrfToken: string | null;
  /** The reset link's real lifetime, for copy that must match the email. */
  resetTtlMinutes: number;
  bootstrapError: string | null;
  retryBootstrap: () => void;
} {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [resetTtlMinutes, setResetTtlMinutes] = useState(30);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setBootstrapError(null);
    api
      .csrfToken()
      .then(({ csrfToken: token, resetTtlMinutes: ttl }) => {
        if (cancelled) return;
        setCsrfToken(token);
        setResetTtlMinutes(ttl);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setBootstrapError(
          error instanceof InteractionApiError ? error.code : 'network',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [api, attempt]);

  const retryBootstrap = useCallback(() => setAttempt((n) => n + 1), []);

  return { csrfToken, resetTtlMinutes, bootstrapError, retryBootstrap };
}
